import React from 'react';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { IconSearch } from '@bynder/icons';
import { ClearButton } from './ClearButton';
import { SearchByImageButton } from './SearchByImage/SearchByImageButton';
import { SearchInput } from './SearchInput';
import { SearchModeDropdown } from './SearchModeDropdown';
import { SEARCH_MIN_CHARS } from '../constants';
import { SEARCH_BAR_HEIGHT_CLOSED, SEARCH_BAR_HEIGHT_OPEN } from './constants';
import { SelectedSearchOptions } from './SelectedSearchOptions';

import {
	useSearchMode,
	useSpotlightDropdownContext,
	useSearch,
	useSearchScope,
} from '../SpotlightState';

interface Props {
	collectionId: string | undefined;
}

export function SearchBar({ collectionId }: Props) {
	const { selectedSearchOptions } = useSearchScope();
	const { searchTerm, onClearButtonClick } = useSearch();
	const showClearButton = searchTerm.length > 0;
	const { isDropdownOpen, openSearchByImage } = useSpotlightDropdownContext();
	const { canUseSimilaritySearch } = useSearchMode();
	const canUseSearchByImage = canUseSimilaritySearch && searchTerm.length < SEARCH_MIN_CHARS;
	const showSelectedSearchOptions = selectedSearchOptions.length > 0 && !isDropdownOpen;

	return (
		<StyledBar $isOpen={isDropdownOpen}>
			<IconSearch size="22px" />

			<StyledInputWrapper>
				{showSelectedSearchOptions && <SelectedSearchOptions />}

				<SearchInput collectionId={collectionId} />
			</StyledInputWrapper>

			<StyledIcons>
				{showClearButton && <ClearButton onClick={onClearButtonClick} />}
			</StyledIcons>
			{canUseSimilaritySearch && <SearchModeDropdown />}
			{canUseSearchByImage && <SearchByImageButton onClick={openSearchByImage} />}
		</StyledBar>
	);
}

const StyledBar = styled.div<{ $isOpen: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	gap: ${token.spacing3};
	width: 100%;
	height: ${({ $isOpen }) => ($isOpen ? SEARCH_BAR_HEIGHT_OPEN : SEARCH_BAR_HEIGHT_CLOSED)}px;
	padding: 6px ${token.spacing4} ${token.spacing3};
	box-sizing: border-box;
`;

const StyledInputWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${token.spacing3};
	flex: 1;
	min-width: 0;
`;

const StyledIcons = styled.div`
	display: flex;
	align-items: center;
	gap: ${token.spacing3};
`;
